import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, ArrowLeft, Loader2, Search, Trash2 } from 'lucide-react';
import { api, LitLegionTaskStatusResponse } from '@/lib/api';
import { useAdminUserTaskStatuses } from '../hooks/useAdminUserTaskStatuses';
import { LL_TASK_STATUSES, isLLTaskStatus } from '../types';

export function AdminUserTaskStatusesPage() {
  const {
    statuses,
    total,
    loading,
    error,
    loadedUsername,
    resolvedUserId,
    fetchForUsername,
    refresh,
    loadMore,
    hasMore,
    applyLocalUpdate,
    applyLocalDelete,
  } = useAdminUserTaskStatuses();

  const [usernameInput, setUsernameInput] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = usernameInput.trim();
    if (!trimmed) return;
    setActionError(null);
    fetchForUsername(trimmed);
  };

  const handleStatusChange = async (taskStatus: LitLegionTaskStatusResponse, newStatus: string) => {
    if (!isLLTaskStatus(newStatus) || newStatus === taskStatus.status) return;

    // Admin override bypasses the player state machine: setting status to
    // `awarded` does NOT grant XP, coin rewards, or badge rewards (those only
    // fire from the player flow). Make sure the admin understands that
    // before we let them put the player into a state that looks earned.
    if (newStatus === 'awarded' || newStatus === 'completed') {
      const proceed = window.confirm(
        `Setting status to "${newStatus}" via admin override does NOT grant XP, ` +
          `coin rewards, or badge rewards. The player will see the task as ` +
          `${newStatus} but will not receive any rewards from this change.\n\n` +
          `Continue?`,
      );
      if (!proceed) return;
    }

    setUpdatingId(taskStatus.taskStatusId);
    setActionError(null);
    try {
      const result = await api.adminUpdateLitLegionTaskStatus(taskStatus.taskStatusId, newStatus);
      applyLocalUpdate(result.taskStatus);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update task status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (taskStatus: LitLegionTaskStatusResponse) => {
    const confirmed = window.confirm(
      `Delete task status ${taskStatus.taskStatusId}? This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingId(taskStatus.taskStatusId);
    setActionError(null);
    try {
      await api.adminDeleteLitLegionTaskStatus(taskStatus.taskStatusId);
      applyLocalDelete(taskStatus.taskStatusId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete task status');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_lit_legion/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin Dashboard
        </Link>
        <h1 className="text-2xl font-semibold">User Task Statuses</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Look up a user's task statuses and override or remove them individually.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          placeholder="Enter username (e.g. devuser1)"
          value={usernameInput}
          onChange={(e) => setUsernameInput(e.target.value)}
          className="max-w-md"
        />
        <Button type="submit" disabled={loading || !usernameInput.trim()}>
          {loading && !loadedUsername ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Look up
        </Button>
        {loadedUsername && (
          <Button type="button" variant="outline" onClick={refresh} disabled={loading}>
            Refresh
          </Button>
        )}
      </form>

      {error && (
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      )}

      {actionError && <p className="text-sm text-destructive">{actionError}</p>}

      {loadedUsername && (
        <div>
          <p className="text-sm text-muted-foreground mb-3">
            {total} status{total !== 1 ? 'es' : ''} for{' '}
            <span className="font-medium text-foreground">{loadedUsername}</span>
            {resolvedUserId && <span className="font-mono text-xs ml-2">({resolvedUserId})</span>}
          </p>

          {loading && statuses.length === 0 ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-lg" />
              ))}
            </div>
          ) : statuses.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">
              No task statuses found for this user.
            </p>
          ) : (
            <div className="space-y-3">
              {statuses.map((s) => (
                <Card key={s.taskStatusId}>
                  <CardContent className="flex items-center justify-between gap-4 py-4">
                    <div className="flex-1 min-w-0 text-sm space-y-1">
                      {s.task ? (
                        <>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium truncate">{s.task.name}</p>
                            {s.task.taskType && (
                              <Badge variant="secondary" className="text-xs shrink-0">
                                {s.task.taskType}
                              </Badge>
                            )}
                            {s.task.category && (
                              <Badge variant="outline" className="text-xs shrink-0">
                                {s.task.category}
                              </Badge>
                            )}
                          </div>
                          {s.task.description && (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {s.task.description}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="font-medium text-muted-foreground italic">(task deleted)</p>
                      )}
                      <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground">
                        {s.completedAt && (
                          <span>completed: {new Date(s.completedAt).toLocaleString()}</span>
                        )}
                        {s.xpAmountReceived !== null && <span>XP: {s.xpAmountReceived}</span>}
                        <span>updated: {new Date(s.updatedAt).toLocaleString()}</span>
                      </div>
                      {s.userInput && (
                        <p className="text-xs text-muted-foreground">
                          input: <span className="text-foreground">{s.userInput}</span>
                        </p>
                      )}
                      <p className="font-mono text-[10px] text-muted-foreground/70 truncate">
                        {s.taskStatusId} · task {s.taskId}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Select
                        value={s.status}
                        onValueChange={(value) => handleStatusChange(s, value)}
                        disabled={updatingId === s.taskStatusId}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LL_TASK_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(s)}
                        disabled={deletingId === s.taskStatusId}
                      >
                        {deletingId === s.taskStatusId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {hasMore && (
                <div className="text-center">
                  <Button variant="ghost" onClick={loadMore} disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load more'
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
